import { useEffect, useMemo, useState } from 'react';
import { applyNaturalPath, naturalizePath, PATH_STYLES, pathLength, pathSections, selfCrosses } from './naturalPath.js';

export default function NaturalPathEditor({ place, onApply, onCancel, onPreview }) {
  const sections = pathSections(place);
  const saved = place.shape.shapeData?.naturalPath;
  const [sectionIndex, setSectionIndex] = useState(0);
  const section = sections[sectionIndex] || [];
  // Reopen the original guide only while its generated geometry is unchanged.
  const anchors = saved?.sectionIndex === sectionIndex &&
    JSON.stringify(saved.generatedPoints) === JSON.stringify(section) ? saved.anchors : section;
  const descriptor = `${place.placeType} ${place.name}`.toLowerCase();
  const [settings, setSettings] = useState(saved?.settings || {
    style: /river|stream|8029/.test(descriptor) ? 'lowland' : /canal/.test(descriptor) ? 'canal' : /trail/.test(descriptor) ? 'trail' : 'road',
    winding: 65, deviation: Math.max(1, Math.round(pathLength(anchors) / 100)),
    bendScale: Math.max(10, Math.round(pathLength(anchors) / 20)), seed: 1,
  });
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(anchors.length - 1);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { onPreview?.(preview); return () => onPreview?.(null); }, [preview, onPreview]);
  const change = (key, value) => { setSettings(current => ({ ...current, [key]: value })); setPreview(null); setError(''); };
  const generate = (tryAnother = false) => {
    const nextSettings = tryAnother ? { ...settings, seed: settings.seed + 1 } : settings;
    setSettings(nextSettings);
    try {
      if (end <= start) throw new Error('The end anchor must follow the start anchor.');
      const generated = naturalizePath(anchors.slice(start, end + 1), nextSettings);
      const samePoint = (a, b) => a.x === b.x && a.z === b.z;
      const startIndex = section.findIndex(p => samePoint(p, anchors[start]));
      const endIndex = section.findIndex((p, i) => i > startIndex && samePoint(p, anchors[end]));
      const points = [...section.slice(0, startIndex), ...generated, ...section.slice(endIndex + 1)];
      if (selfCrosses(points)) throw new Error('These bends cross another part of the path. Reduce deviation or try another variation.');
      setPreview(points); setError('');
    } catch (failure) { setPreview(null); setError(failure.message); }
  };
  const drawing = useMemo(() => {
    const all = [...anchors, ...(preview || [])];
    if (!all.length) return null;
    const minX = Math.min(...all.map(p => p.x)), maxX = Math.max(...all.map(p => p.x));
    const minZ = Math.min(...all.map(p => p.z)), maxZ = Math.max(...all.map(p => p.z));
    const scale = Math.min(680 / Math.max(1, maxX - minX), 240 / Math.max(1, maxZ - minZ));
    const project = points => points.map(p => `${20 + (p.x - minX) * scale},${260 - (p.z - minZ) * scale}`).join(' ');
    return { guide: project(anchors), result: preview ? project(preview) : '',
      anchors: anchors.map(p => ({ x: 20 + (p.x - minX) * scale, y: 260 - (p.z - minZ) * scale })) };
  }, [anchors, preview]);
  return <div className="editor-section" aria-label="Naturalize path">
    <h4>Naturalize {place.name || 'path'}</h4>
    <p className="panel-copy">Keep the guide points fixed and add bends between them. Select a range to leave the rest untouched. Distances are in feet.</p>
    <div className="editor-grid editor-grid-four">
      {sections.length > 1 && <label className="editor-field">Path section
        <select aria-label="Path section" className="select-input" value={sectionIndex} onChange={event => {
          const index = Number(event.target.value); setSectionIndex(index); setStart(0);
          setEnd(sections[index].length - 1); setPreview(null);
        }}>{sections.map((_, i) => <option key={i} value={i}>Section {i + 1}</option>)}</select>
      </label>}
      <label className="editor-field">Style<select className="select-input" aria-label="Path style" value={settings.style}
        onChange={event => change('style', event.target.value)}>
        {Object.entries(PATH_STYLES).map(([key, profile]) => <option key={key} value={key}>{profile.label}</option>)}
      </select></label>
      <label className="editor-field">Winding ({settings.winding}%)<input aria-label="Winding" type="range" min="0" max="100"
        value={settings.winding} onChange={event => change('winding', Number(event.target.value))} /></label>
      <label className="editor-field">Maximum deviation (ft)<input aria-label="Maximum deviation (ft)" className="text-input" type="number" min="0"
        value={settings.deviation} onChange={event => change('deviation', Number(event.target.value))} /></label>
      {[['Start anchor', start, setStart], ['End anchor', end, setEnd]].map(([label, value, setter]) =>
        <label key={label} className="editor-field">{label}<select aria-label={label} className="select-input" value={value}
          onChange={event => { setter(Number(event.target.value)); setPreview(null); }}>
          {anchors.map((_, i) => <option key={i} value={i}>Anchor {i + 1}</option>)}
        </select></label>)}
    </div>
    <details><summary>Advanced</summary><div className="editor-grid editor-grid-four">
      <label className="editor-field">Bend scale (ft)<input aria-label="Bend scale (ft)" className="text-input" type="number" min="1"
        value={settings.bendScale} onChange={event => change('bendScale', Number(event.target.value))} /></label>
      <label className="editor-field">Seed<input aria-label="Seed" className="text-input" type="number" step="1"
        value={settings.seed} onChange={event => change('seed', Number(event.target.value))} /></label>
    </div></details>
    {drawing && <svg viewBox="0 0 720 280" role="img" aria-label="Original guide in gray and generated path in blue"
      style={{ width: '100%', maxHeight: 300, background: '#f8fbff', marginTop: 12 }}>
      <polyline points={drawing.guide} fill="none" stroke="#929eaa" strokeWidth="2" strokeDasharray="5 5" />
      {preview && <polyline points={drawing.result} fill="none" stroke="#1677bd" strokeWidth="2" />}
      {drawing.anchors.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="3" fill="#465668"><title>Anchor {i + 1}</title></circle>)}
    </svg>}
    <p className="panel-copy" aria-live="polite">Guide: {(pathLength(anchors) / 5280).toFixed(2)} mi.
      {preview && ` Generated: ${(pathLength(preview) / 5280).toFixed(2)} mi.`} Gray dashed: guide. Blue: preview.</p>
    {error && <p role="alert">{error}</p>}
    <div className="button-row compact">
      <button type="button" className="secondary-button" onClick={() => generate()}>Preview</button>
      <button type="button" className="secondary-button" onClick={() => generate(true)}>Try another</button>
      <button type="button" className="primary-button" disabled={!preview} onClick={() =>
        onApply(applyNaturalPath(place, preview, sectionIndex, settings, anchors))}>Apply to draft</button>
      <button type="button" className="secondary-button" onClick={onCancel}>Cancel</button>
    </div>
    <p className="panel-copy">Apply updates the draft. Save the place to keep it. This shapes the course without checking terrain or downhill flow.</p>
  </div>;
}
